import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, Urdu translation of en.tsx. Every number comes from `limits`; every claim
 * about who can read what follows the threat model (PLAN.md 4.12). Keep it in step with en.tsx.
 */
export function GuideUr({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>Vrelis کیسے کام کرتا ہے</h1>
      <p className="lead">
        Vrelis خفیہ پیغامات، یعنی ٹائم کیپسول، بھیجنے کی سروس ہے جو صرف آپ کے چنے ہوئے لمحے سے پڑھے جا سکتے ہیں: آزمائش کے سب ارکان، یا ایک
        شخص۔ اس لمحے سے پہلے کوئی کیپسول نہیں پڑھ سکتا: نہ وہ شخص جسے آپ نے لکھا، نہ Vrelis چلانے والے، نہ آپ۔
      </p>
      <p className="warn">
        یہ ایک بند آزمائش ہے۔ جو کیپسول آپ رکھنا چاہتے ہیں انہیں ڈاؤن لوڈ کر لیں (نیچے دیکھیں)، اور اپنا بازیابی کا فقرہ سنبھال کر رکھیں۔
      </p>

      <h2>قفل پر بھروسا کیوں کیا جا سکتا ہے</h2>
      <p>
        قفل Vrelis کا وعدہ نہیں ہے۔ یہ شائع شدہ cryptography کے ساتھ ایک عوامی وقت کا سگنل ہے جو Vrelis کے اختیار میں نہیں۔ یہاں بتایا گیا ہے کہ اسے کیا قائم رکھتا ہے،
        اور اس کے ناکام ہونے کے لیے آپ کو کیا ماننا پڑے گا۔
      </p>

      <h3>drand: ایک عوامی گھڑی جو وقت پر دستخط کرتی ہے</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext> ایک نیٹ ورک ہے جسے{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext> چلاتی ہے، آزاد اداروں کا ایک گروپ (ان میں Cloudflare، Protocol Labs، Kudelski
          Security، EPFL اور University College London شامل ہیں) جو مل کر ہر 3 سیکنڈ میں ایک بے ترتیب، دستخط شدہ قدر شائع کرتے ہیں۔ قدروں کے نمبر ہوتے ہیں: دور 1،
          دور 2، وغیرہ، ہر ایک ایک معلوم لمحے کے لیے۔ کوئی بھی انہیں کئی عوامی relays سے پڑھ سکتا ہے؛ کوئی مستقبل کی قدر نہیں پڑھ سکتا، کیونکہ وہ ابھی
          موجود ہی نہیں۔
        </li>
        <li>
          ہر دور پر threshold cryptography سے دستخط ہوتے ہیں (<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">BLS دستخط</Ext>): ہر ادارے کے پاس ایک خفیہ کلید کا ایک حصہ ہوتا ہے، اور دستخط کے لیے
          حصوں کی اکثریت چاہیے۔ کوئی اکیلا ادارہ، اور اداروں کی کوئی اقلیت، کسی دور کا دستخط اس کے وقت سے پہلے نہیں بنا سکتی۔ یہ نیٹ ورک
          2020 سے چل رہا ہے؛ ہم جو chain استعمال کرتے ہیں،{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>، وہ 2023 سے۔
          اس کی عوامی کلید ہمارے کوڈ میں طے شدہ ہے، اور ہمیں ملنے والا ہر سگنل اسی سے جانچا جاتا ہے۔
        </li>
        <li>
          <b>وقت کے قفل والی خفیہ کاری</b> (<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>، جسے Protocol Labs اور Cloudflare کے محققین نے
          2023 میں شائع کیا) اس گھڑی کو قفل بنا دیتی ہے: پیغام کو
          ایک دور کے نمبر کے لیے خفیہ کیا جاتا ہے، اور اس دور کا دستخط، شائع ہونے کے بعد، واحد کلید ہے جو اسے کھولتی ہے۔ جب تک drand اسے شائع نہ کرے، وہ کلید
          دنیا میں کہیں موجود نہیں۔
        </li>
      </ul>

      <h3>Vrelis اسے کیسے استعمال کرتا ہے</h3>
      <ul>
        <li>
          آپ کا براؤزر متن اور آپ کی فائلوں کی کلیدیں آپ کے چنے ہوئے وقت کے drand دور کے لیے خفیہ کرتا ہے؛ کیپسول لکھیں کا صفحہ بھیجنے سے پہلے دور کا نمبر دکھاتا ہے۔
          نجی کیپسول دوسری بار وصول کنندہ کی کلید کے لیے خفیہ کیا جاتا ہے، اس لیے وقت کے بعد صرف وہی اسے کھول سکتا ہے۔
        </li>
        <li>
          ہمارے سرور کو صرف مہر بند نتیجہ ملتا ہے اور وہ اسے محفوظ کرتا ہے۔ اس کے پاس کوئی کلید نہیں جو کیپسول کھولے: نہ وقت سے پہلے، اور نجی کیپسول کے لیے
          وقت کے بعد بھی نہیں۔ ڈیٹا بیس لیک ہونے سے خفیہ متن اور میٹا ڈیٹا سامنے آئے گا، کبھی کوئی پیغام نہیں۔
        </li>
        <li>
          جب وقت آتا ہے تو آپ کا براؤزر (یا آف لائن کھولنے والا ٹول) چار عوامی drand relays میں سے کسی ایک سے دور کا دستخط لاتا ہے، اسے
          طے شدہ عوامی کلید سے جانچتا ہے، اور کھول دیتا ہے۔ اس مرحلے میں Vrelis کا کوئی کردار نہیں۔
        </li>
        <li>
          ہر کیپسول پر اس کے مصنف کی کلید کے دستخط ہوتے ہیں (<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>، اسی قسم کی کلید جو Bitcoin
          اور <Ext href="https://nostr.com">Nostr</Ext> استعمال کرتے ہیں)، اس لیے بدلا ہوا کیپسول پکڑا جاتا ہے۔ کلید آپ کے براؤزر میں آپ کے 12 الفاظ سے بنتی ہے (
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>) اور کبھی غیر خفیہ حالت میں اس سے باہر نہیں جاتی۔
        </li>
      </ul>

      <h3>اگر Vrelis ختم ہو جائے</h3>
      <ul>
        <li>
          فارمیٹس کھلے ہیں: کیپسول ایک <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">Nostr event</Ext> ہے (نجی کیپسول کے لیے{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">NIP-59 gift wrap</Ext>) جس کا مواد ایک{" "}
          <Ext href="https://age-encryption.org">age</Ext> فائل ہے جس میں <Ext href="https://github.com/drand/tlock-js">tlock</Ext> stanza ہوتا ہے۔ جو بھی سافٹ ویئر
          ان کھلی تفصیلات پر عمل کرے وہ آپ کے کیپسول کھول سکتا ہے؛ ہمارا سافٹ ویئر ضروری نہیں۔
        </li>
        <li>
          <b>ڈاؤن لوڈ</b> کیپسول آپ کی اپنی ڈسک پر رکھتا ہے، آف لائن کھولنے والے ٹول کے ساتھ: ایک HTML فائل جو انہیں آپ کے کمپیوٹر پر کھولتی ہے، کسی
          Vrelis سرور کے بغیر، ضروری drand سگنل اور، نجی کیپسول کے لیے، آپ کی کلید استعمال کر کے۔ مکمل export کو vrelis.com کے بغیر کھولا جا سکتا ہے اگر
          ضروری سگنل دستیاب ہو اور آپ اس کی ضروری فائلیں اور کلیدیں سنبھال کر رکھیں۔
        </li>
        <li>
          اگر drand خود کبھی ضروری مستقبل کا سگنل بنانے سے پہلے رک جائے، تو وہ کیپسول ہمیشہ کے لیے مقفل رہ سکتے ہیں۔ انہیں پہلے سے ڈاؤن لوڈ
          کرنے سے یہ خطرہ ختم نہیں ہوتا۔ جس کیپسول کے کھلنے کی تاریخ گزر چکی ہو اسے بھی اپنا مخصوص جانچا ہوا سگنل چاہیے: اگر وہ سگنل آپ کے
          آرکائیو میں نہ ہو یا کسی relay سے دستیاب نہ ہو تو صرف تاریخ سے آپ اسے آف لائن نہیں کھول سکتے۔ مکمل exports اور ان کی ضروری کلیدیں سنبھال کر رکھیں۔
        </li>
      </ul>

      <h3>اس کے ناکام ہونے کے لیے آپ کو کیا ماننا پڑے گا</h3>
      <ul>
        <li>
          یہ کہ drand اداروں کی اکثریت مل کر کسی دور پر وقت سے پہلے دستخط کر دے۔ وہ آزاد ہیں، کئی ملکوں میں ہیں، اور chain عوامی ہے، اس لیے
          ایسا دستخط سب کو نظر آئے گا۔ پورے نظام کا بھروسا اسی مفروضے پر ہے۔
        </li>
        <li>
          یہ کہ ویب صفحہ جھوٹ بولے۔ صفحہ وہ کوڈ ہے جو Vrelis آپ کے براؤزر کو دیتا ہے؛ جو اس کوڈ پر قابو رکھے وہ مہر بند ہونے سے پہلے متن پڑھ سکتا ہے، یا آپ کی
          کلید اسے کھولنے کے بعد۔ اسی لیے کیپسول ڈاؤن لوڈ کر کے آف لائن کھولے جا سکتے ہیں، اسی لیے کلیدوں کے فنگر پرنٹ دکھائے جاتے ہیں تاکہ آپ Vrelis کے باہر
          ان کا موازنہ کر سکیں، اور اسی لیے آزمائش کے بعد ایک فون ایپ کا منصوبہ ہے جو کلید کو ویب صفحے سے دور رکھے۔
        </li>
        <li>
          یہ کہ آج کی cryptography ٹوٹ جائے۔ Elliptic-curve cryptography، جو drand اور آپ کی کلید دونوں استعمال کرتے ہیں، مستقبل کے{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">کوانٹم کمپیوٹرز</Ext> سے ٹوٹ سکتی ہے۔
          دہائیوں تک مہر بند رہنے والے کیپسول کے لیے یہ ایک حقیقی خطرہ ہے جو آزمائش حل نہیں کرتی۔
        </li>
      </ul>

      <h2>شامل ہونا</h2>
      <ol>
        <li>
          اگر ایڈمن نے آپ کا پتہ منظور کیا ہے تو براہ راست اسی سے رجسٹر کریں؛ ورنہ اپنی دعوت کا لنک کھولیں اور اسی کا پتہ استعمال کریں۔ اسی Google اکاؤنٹ سے لاگ ان کریں (Google، Vrelis کو صرف آپ کا
          نام اور ای میل بتاتا ہے)، یا پاس ورڈ سے (ہم پتے کی تصدیق کے لیے لنک ای میل کرتے ہیں)۔ بعد میں آپ لاگ ان کے لیے پاس کی شامل کر سکتے ہیں۔
        </li>
        <li>
          Vrelis آپ کے براؤزر میں آپ کی <b>کلید</b> بنانے کی پیشکش کرتا ہے: آپ کا بنیادی دستخط، جو آپ کے بھیجے ہوئے پر دستخط کرتا ہے اور آپ کو بھیجے گئے نجی کیپسول کھولتا ہے۔ آپ
          اسے چھوڑ کر بعد میں اکاؤنٹ، کلیدیں میں بنا سکتے ہیں؛ کلید کے بغیر آپ صرف عوامی کیپسول پڑھتے ہیں۔ آپ کا <b>بازیابی کا فقرہ</b> (12 الفاظ جو
          کسی بھی آلے پر کلید دوبارہ بناتے ہیں) ظاہر ہونے سے پہلے آپ سے کہا جاتا ہے کہ یقینی بنائیں کوئی آپ کی اسکرین نہ دیکھ سکے نہ ریکارڈ کر سکے۔
        </li>
        <li>12 الفاظ کاغذ پر لکھیں۔ آپ سے ان میں سے تین واپس لکھنے کو کہا جاتا ہے، پھر یہ تصدیق کے لیے کہ یہ آپ ہیں دوبارہ لاگ ان کرنے کو: اپنے پاس ورڈ یا پاس کی سے، یا اپنی بالکل پہلی کلید کے لیے Google سے۔</li>
        <li>
          PIN چنیں (کم از کم {l.pinDigits} ہندسے) اور ایک <Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">پاس کی</Ext> بنائیں (فنگر پرنٹ،
          چہرہ یا آپ کے آلے کا PIN)۔ یہ دونوں مل کر اس آلے پر کلید کی حفاظت کرتے ہیں
          اور اس نقل کی بھی جو Vrelis نئے آلے پر منتقلی کے لیے رکھتا ہے۔ اگر آپ کا براؤزر پاس کی کو اس طرح استعمال نہ کر سکے تو کلید اس براؤزر میں بغیر حفاظت کے رہتی ہے اور
          سرور پر کوئی نقل نہیں ہوتی؛ تب دوسرے آلے تک جانے کا واحد راستہ فقرہ ہے۔
        </li>
      </ol>

      <h2>آپ کا بازیابی کا فقرہ</h2>
      <ul>
        <li>اسے کاغذ پر، کسی محفوظ جگہ رکھیں۔ اس کی تصویر نہ لیں اور اسے کمپیوٹر یا پاس ورڈ مینیجر میں محفوظ نہ کریں۔</li>
        <li>
          جس کے پاس 12 الفاظ ہیں وہ <b>آپ ہی</b> ہے: وہ آپ کے طور پر دستخط کر سکتا ہے اور آپ کے نجی کیپسول کھول سکتا ہے۔ اگر یہ لیک ہو جائیں تو اکاؤنٹ،
          کلیدیں میں <b>کلید ری سیٹ کریں</b> استعمال کریں: آپ فقرہ لکھتے ہیں، دوبارہ لاگ ان کرتے ہیں، اور کلید ہمیشہ کے لیے منسوخ ہو جاتی ہے؛ اب کوئی اس سے یا اس کو کچھ نہیں بھیج سکتا۔ آپ بتاتے ہیں کہ یہ کب سے
          چوری ہو سکتی ہے، اور اس وقت سے اس سے بھیجے گئے کیپسول پر "منسوخ شدہ کلید" کا نشان لگ جاتا ہے۔ پھر آپ نئے فقرے کے ساتھ نئی کلید بناتے ہیں۔
        </li>
        <li>
          آپ کی پچھلی کلیدیں ایک <b>کلیدوں کے گچھے</b> میں جا سکتی ہیں، جو آپ کی موجودہ کلید سے خفیہ ہوتا ہے، تاکہ پرانی کلید کو بھیجے گئے کیپسول پرانے فقرے کے بغیر بھی کھل سکیں۔
          آپ پرانا فقرہ خود بھی رکھ سکتے ہیں اور بعد میں اسے کلیدوں کے گچھے میں شامل کر سکتے ہیں۔
        </li>
        <li>
          اگر آلہ گم ہو جائے: کسی دوسرے آلے پر لاگ ان کریں اور اپنی پاس کی اور PIN (اگر آپ کی پاس کی ہم آہنگ ہوتی ہے) یا اپنا فقرہ استعمال کریں۔ اگر PIN بھول جائیں:
          فقرہ استعمال کریں۔ {l.pinAttempts} غلط PIN کے بعد سرور کی نقل بلاک ہو جاتی ہے؛ فقرہ نئی نقل لکھتا ہے۔
        </li>
        <li>
          اگر آپ فقرہ <b>اور</b> اپنی کلید والا ہر آلہ کھو دیں تو آپ کے نجی کیپسول کبھی نہیں کھل سکتے، کسی سے بھی نہیں۔ ایڈمن آپ کو صرف
          نئی شناخت دے سکتا ہے۔ کلید منسوخ کرنے کے لیے بھی فقرہ چاہیے، اس لیے اس کے بغیر ایڈمن آپ کے لیے اسے منسوخ کرتا ہے۔
        </li>
      </ul>

      <h2>کیپسول لکھنا</h2>
      <ul>
        <li>
          <b>آزمائش کے سب ارکان</b> (عوامی) یا <b>ایک رکن</b> (نجی)۔ عوامی کیپسول اپنے وقت کے بعد ہر رکن پڑھ سکتا ہے؛ نجی کیپسول صرف
          وہ شخص جسے آپ نے لکھا، اور آپ، اگر آپ نقل رکھیں (پہلے سے آن)۔
        </li>
        <li>
          ہر کیپسول کا <b>موضوع</b> ضروری ہے۔ یہ کبھی خفیہ نہیں ہوتا: کیپسول دیکھنے والا ہر شخص اسے فوراً پڑھ لیتا ہے، ہمارا سرور بھی۔
        </li>
        <li>
          <b>تعارف</b> اختیاری ہے: زیادہ سے زیادہ {l.introChars} حروف جو وقت سے پہلے، کیپسول پر کلک کرنے پر پڑھے جا سکتے ہیں۔ عوامی کیپسول پر
          اسے ہر کوئی پڑھتا ہے، ہمارا سرور بھی۔ نجی کیپسول پر اسے صرف وصول کنندہ اپنی کلید سے پڑھتا ہے؛ سرور اسے نہیں دیکھتا۔ پیغام اور اس کی
          فائلیں دونوں صورتوں میں مقفل رہتی ہیں۔
        </li>
        <li>
          نجی کیپسول کس نے بھیجا، یہ وصول کنندہ کی کلید کے لیے مہر بند ہوتا ہے: وہ اسے اپنی کلید سے کیپسول کھولتے ہی دیکھ لیتے ہیں، اس کے وقت سے بہت پہلے۔
          جس کے پاس وہ کلید کے بغیر کیپسول ہو وہ صرف موضوع اور وقت دیکھتا ہے۔ ہمارا سرور جانتا ہے کہ کس اکاؤنٹ نے اسے بھیجا۔ یہ صرف وصول کنندہ کو بتاتا ہے، اس کی کلید کے لیے خفیہ کر کے، تاکہ ایپ جانچ سکے کہ کیپسول پر دستخط کرنے والی کلید اسی اکاؤنٹ کی ہے؛ ورنہ یہ نام کی جگہ "غیر تصدیق شدہ بھیجنے والا" دکھاتی ہے۔
        </li>
        <li>
          <b>بھیجنے والے کا نام چھپائیں</b> (نجی کیپسول): وصول کنندہ کی فہرستوں میں کیپسول پر کلک کرنے تک آپ کے نام کی جگہ "پوشیدہ بھیجنے والا" دکھتا ہے، تاکہ
          ان کے کندھے کے اوپر سے دیکھنے والا کوئی نہ جانے کہ انہیں کون لکھتا ہے۔ وصول کنندہ پھر بھی وقت سے پہلے دیکھ سکتا ہے کہ یہ آپ ہیں۔
        </li>
        <li>
          چنیں کہ یہ کب کھلے: 5 منٹ، ایک گھنٹے، ایک دن، ایک ہفتے، ایک سال میں، یا سال 2125 تک کی کوئی تاریخ۔ اصل کھلنے کا وقت بھیجنے سے پہلے دکھایا جاتا ہے؛ یہ
          چند سیکنڈ تک درست ہوتا ہے۔
        </li>
        <li>
          متن: عوامی کیپسول کے لیے زیادہ سے زیادہ {l.publicText}، نجی کے لیے {l.privateText}۔ فائلیں: زیادہ سے زیادہ {l.files} فائلیں اور کل ملا کر مفت پر {l.fileBytes} یا پرانا یا اسٹارٹ کے ساتھ {l.planFileBytes}۔ تصاویر
          چھوٹی کی جاتی ہیں اور مقام سمیت ان کا میٹا ڈیٹا ہٹا دیا جاتا ہے۔
        </li>
        <li>
          <b>مہر بند کر کے بھیجیں</b> کیپسول پر دستخط کے لیے ایک بار آپ کا PIN اور پاس کی مانگتا ہے۔ اس کے بعد کوئی اسے بدل نہیں سکتا، اور کوئی اسے اس کے
          وقت سے پہلے نہیں پڑھ سکتا۔
        </li>
        <li>
          کسی کو نجی طور پر لکھنے سے پہلے اس کی کلید چیک کریں: کیپسول لکھیں کا صفحہ اس کا فنگر پرنٹ (16 حروف) دکھاتا ہے۔ اگر اس براؤزر کے آخری بار دیکھنے کے بعد اس کی کلید
          بدل گئی ہو تو یہ صفحہ آپ کو خبردار کرتا ہے اور تب تک انتظار کرتا ہے جب تک آپ Vrelis کے باہر اس سے تصدیق نہ کر لیں کہ نئی کلید واقعی اسی کی ہے۔
        </li>
      </ul>

      <h2>پڑھنا</h2>
      <ul>
        <li>
          <b>عوامی پیغامات</b>: عوامی کیپسول۔ <b>موصولہ پیغامات</b>: آپ کے نام نجی کیپسول۔ <b>بھیجے گئے</b>: آپ کے کیپسول۔ <b>زیر نظر</b>: وہ عوامی کیپسول جن کا آپ انتظار کرتے ہیں۔ کسی
          مصنف کو فالو کریں تاکہ صرف اس کے کیپسول دیکھیں۔
        </li>
        <li>
          کسی رکن کو <b>چھپائیں</b>: اس کے عوامی کیپسول ہر آلے پر آپ کے عوامی پیغامات سے نکل جاتے ہیں؛ اس کے لیے کچھ نہیں بدلتا۔ کسی رکن کو <b>بلاک کریں</b>: وہ اب
          آپ کے موصولہ پیغامات میں نجی کیپسول نہیں ڈال سکتا (کیپسول لکھیں کا صفحہ اسے بھیجنے سے پہلے بتا دیتا ہے)؛ آپ پھر بھی اسے فالو کر سکتے ہیں اور اس کے عوامی کیپسول پڑھ سکتے ہیں۔
          اس کے پچھلے کیپسول موصولہ پیغامات، بلاک شدہ میں چلے جاتے ہیں، اور پھر بھی کھلتے ہیں۔ دونوں کسی شخص کے مینو میں اور لوگ کے صفحے پر ہیں، اس کے ساتھ کہ کون آپ کو فالو کرتا ہے۔
          Vrelis صرف اسی کی ذمہ داری لیتا ہے جو اسے ملتا ہے اور جو وہ پہنچاتا ہے: کوئی کسی کو Vrelis کے باہر آپ کی عوامی کلید کے لیے خفیہ کرنے سے نہیں روک سکتا۔
        </li>
        <li>
          نجی کیپسول کھولنے تک کچھ نہیں دکھاتا: <b>کھولیں</b> آپ کا PIN مانگتا ہے، پھر دکھاتا ہے کہ اسے کس نے بھیجا اور یہ کب کھلتا ہے، اور متن تب جب وہ
          وقت آ جائے۔
        </li>
        <li>
          آپ کی کلید مقفل رہتی ہے؛ ہر وہ عمل جسے اس کی ضرورت ہو PIN مانگتا ہے۔ کئی کیپسول لگاتار پڑھنے کے لیے اوپر والی پٹی میں <b>… منٹ کے لیے کھولیں</b> استعمال
          کریں۔ <b>مقفل کریں</b> ہر نجی چیز فوراً چھپا دیتا ہے؛ صفحہ چھوڑنا بھی یہی کرتا ہے۔
        </li>
        <li>
          <a href="/account">اکاؤنٹ</a>، رازداری میں: "میری فہرستوں میں بھیجنے والے صرف کیپسول پر کلک کرنے کے بعد دکھائیں" ہر نجی کیپسول کو پوشیدہ بھیجنے والے والا سمجھتا ہے،
          ہر آلے پر۔
        </li>
        <li>
          فہرستیں ایک وقت میں 50 کیپسول دکھاتی ہیں؛ <b>مزید دکھائیں</b> اگلے شامل کرتا ہے۔ Vrelis انہیں فلٹر اور ترتیب دیتا ہے (کھلنے کے وقت سے یا اس وقت سے جب اسے
          ملے)، اس لیے فلٹر یا گروپ Vrelis کو آپ کے چنے ہوئے ارکان کی ids بھیجتا ہے، کبھی گروپ یا اس کا نام نہیں۔ موصولہ پیغامات کو بھیجنے والے کے لحاظ سے فلٹر کرنے کے لیے
          آپ کی کلید چاہیے، اس میں پوشیدہ بھیجنے والے والے کیپسول شامل نہیں ہوتے، اور جب بھیجنے والے صرف کلک کے بعد دکھتے ہوں تو یہ بند رہتا ہے۔
        </li>
        <li>
          <b>تلاش</b> موضوعات، عوامی کیپسول کا تعارف، عوامی کیپسول کس نے لکھا اور آپ نے کس کو بھیجا، یہ دیکھتی ہے۔ یہ کبھی کھولا گیا متن نہیں دیکھتی
          (وہ صرف آپ کے آلے کے پاس ہے) اور نہ یہ کہ آپ کو نجی کیپسول کس نے بھیجا۔
        </li>
      </ul>

      <h2>جوابی لفافہ، CC اور خط و کتابت</h2>
      <ul>
        <li>
          نجی پیغام کے ساتھ <b>جوابی لفافہ</b> ہو سکتا ہے: آپ ہر وصول کنندہ سے اپنے لیے ایک نجی جواب مانگتے ہیں، آپ کی کلید کے لیے مہر بند۔
          وہ جواب دے سکتے ہیں چاہے آپ کو فالو نہ کرتے ہوں۔ آپ چنتے ہیں کہ ہر جواب آپ کے مقرر کردہ وقت پر کھلے (اس وقت کے بعد کوئی جواب
          قبول نہیں ہوتا) یا وصول کنندہ کے چنے ہوئے وقت پر۔ آپ کیپسول اور جواب کی ادائیگی پہلے سے کرتے ہیں: فی وصول کنندہ 1 کے بجائے 2 ڈاک ٹکٹ۔ وصول کنندہ کا پہلا جواب اس کے لیے مفت ہے؛ دوسرے پر اس کا ایک ڈاک ٹکٹ لگتا ہے، اور آپ دونوں دیکھتے ہیں۔
        </li>
        <li>
          <b>CC</b>: ایک رکن کے لیے نجی پیغام ایک ساتھ مزید ارکان کو جا سکتا ہے، اور فہرست میں شامل ہر کوئی دیکھتا ہے کہ اور کس کو ملا۔ فہرست
          بھیجنے والے کے دستخط کے ساتھ پیغام کے اندر مہر بند ہوتی ہے؛ ہمارا سرور اسے کبھی نہیں دیکھتا۔ CC کے بغیر گروپ ارسال پوشیدہ رہتا ہے: کوئی نہیں دیکھتا کہ
          اور کس کو ملا۔
        </li>
        <li>
          جوابی لفافے والے پیغامات اور ان کے جوابات <b>خط و کتابت</b> کے صفحے پر رہتے ہیں، موصولہ پیغامات یا بھیجے گئے میں نہیں: ہر
          درخواست کے نیچے آپ ہر وصول کنندہ کے جوابات ترتیب سے دیکھتے ہیں، ان کے وقت تک مقفل، اور یہ کہ ہر ایک کی ادائیگی کس نے کی۔ جواب صرف بھیجنے والے کو جاتا ہے۔
        </li>
      </ul>

      <h2>Vrelis کے بغیر کیپسول رکھنا</h2>
      <ul>
        <li>
          کیپسول پر <b>ڈاؤن لوڈ کریں</b> اسے فائل کے طور پر محفوظ کرتا ہے؛ منسلکات کے ساتھ یہ فائلوں اور آف لائن کھولنے والے ٹول سمیت ایک .zip ہوتی ہے۔ کئی منتخب کریں اور ایک آرکائیو کے لیے{" "}
          <b>منتخب ڈاؤن لوڈ کریں</b> استعمال کریں ("دکھائے گئے سب منتخب کریں" دکھائے گئے کیپسول لیتا ہے)۔
        </li>
        <li>
          آف لائن کھولنے والا ٹول ایک HTML فائل ہے (<a href="/account">اکاؤنٹ</a>، یا .zip کے اندر)۔ یہ آپ کے کیپسول آپ کے اپنے کمپیوٹر پر کھولتا ہے، نجی کیپسول آپ کے فقرے سے۔ یہ
          کبھی vrelis.com سے رابطہ نہیں کرتا؛ اگر فائل میں کھلنے کا سگنل ابھی نہ ہو تو یہ صرف عوامی drand وقت کی سروس سے اسے مانگتا ہے۔
        </li>
        <li>
          "کھولا گیا متن اور فائلیں شامل کریں" کھولا گیا متن اور فائلیں ڈاؤن لوڈ میں غیر خفیہ رکھتا ہے: فائل رکھنے والا کوئی بھی انہیں پڑھ سکتا ہے۔
        </li>
      </ul>

      <h2>یہ آلہ</h2>
      <ul>
        <li>
          <b>مقفل کریں</b>: کلید کو میموری سے ہٹاتا ہے۔ <b>لاگ آؤٹ</b>: یہ سیشن ختم کرتا ہے؛ کلید اس آلے پر رہتی ہے۔ <b>تمام آلات سے لاگ آؤٹ</b>:
          ہر آلے پر آپ کے سیشن ختم کرتا ہے۔ <b>اس آلے کا ڈیٹا مٹائیں</b>: آپ کی کلید اور وہ سب کچھ حذف کرتا ہے جو Vrelis نے اس براؤزر میں محفوظ کیا۔
        </li>
        <li>لاگ ان {l.sessionDays} دن تک رہتا ہے۔</li>
      </ul>

      <h2>Vrelis کیا دیکھ سکتا ہے اور کیا نہیں</h2>
      <ul>
        <li>
          <b>Vrelis نہیں پڑھ سکتا</b>: کوئی کیپسول اس کے وقت سے پہلے، یا کوئی نجی کیپسول کبھی بھی (اس کے پاس اس کی کلید نہیں)، یا آپ کی کلید، PIN یا فقرہ۔
        </li>
        <li>
          <b>Vrelis دیکھتا ہے</b>: آپ کا نام اور ای میل (مالک اور ایڈمن ای میل دیکھتے ہیں)، کیپسول کون اور کس کو بھیجتا ہے، یہ کب بھیجا گیا، عوامی کیپسول کب
          کھلتا ہے، سائز، اور یہ کہ کیپسول میں فائلیں ہیں۔ اپنے وقت کے بعد عوامی کیپسول ہر رکن پڑھ سکتا ہے، اس لیے Vrelis بھی۔
        </li>
        <li>
          کلیدوں کا گچھا اور آپ کی کلید کی نقلیں صرف خفیہ حالت میں محفوظ ہوتی ہیں؛ Vrelis انہیں نہیں کھول سکتا۔
        </li>
        <li>
          دستخط ثابت کرتا ہے کہ کس کلید نے کیپسول لکھا۔ "منسوخ شدہ کلید" کے نشان والے کیپسول پر ایسی کلید کے دستخط ہیں جسے اس کے مالک نے بعد میں چوری شدہ بتایا۔ کلید کسی شخص کی ہے، اس کی بنیاد ارکان کی فہرست اور اس نام پر ہے جو بھیجنے والے نے
          کیپسول میں ڈالا۔ جن لوگوں کو آپ نجی طور پر لکھتے ہیں ان کے ساتھ فنگر پرنٹ چیک کریں۔
        </li>
      </ul>

      <h2>منصوبے اور ڈاک ٹکٹ</h2>
      <ul>
        <li>
          ایک <b>ڈاک ٹکٹ</b> ایک وصول کنندہ کو ایک ترسیل کی ادائیگی ہے۔ آپ کی اپنی نقل مفت ہے۔ مفت منصوبہ ماہانہ {l.freeStamps} ڈاک ٹکٹ، {l.freeStorage} اسٹوریج اور فی پیغام فائلوں کے لیے {l.fileBytes} دیتا ہے؛ پرانا منصوبہ
          ({l.legacyStamps} ڈاک ٹکٹ ماہانہ، {l.planStorage}، {l.planFileBytes}) پہلے ارکان کو دیا گیا؛ اسٹارٹ ماہانہ {l.planStamps} ڈاک ٹکٹ دے گا۔ ایک مہینے کے ڈاک ٹکٹ آگے منتقل نہیں ہوتے، اور وہ اگلے مہینے کے
          پہلے دن (UTC) دوبارہ آ جاتے ہیں۔ <b>بلنگ</b> دکھاتا ہے کہ آپ کے پاس کیا ہے، کیا باقی ہے اور کیا منصوبہ بند ہے۔
        </li>
        <li>
          فی الحال کوئی رقم نہیں لی جاتی اور کچھ خریدا نہیں جا سکتا: منصوبے صرف پیش منظر ہیں، اور ایڈمن کوئی درخواست ہاتھ سے فعال کرتا ہے۔ جو آپ پہلے بھیج چکے ہیں وہ آپ کے لیے اور ان لوگوں کے لیے قابل مطالعہ رہتا ہے جنہیں آپ نے بھیجا، آپ کے منصوبے یا ڈاک ٹکٹوں
          کے ساتھ جو بھی ہو۔
        </li>
        <li>
          اپنے لیے نقل اختیاری ہے اور پہلے سے آن ہوتی ہے۔ اگر آپ اسے بند کریں تو وہ پیغام صرف وصول کنندہ ہی کبھی پڑھ سکتا ہے: آپ خود اسے نہیں پڑھ سکیں گے، کھلنے کے بعد بھی نہیں۔
        </li>
      </ul>

      <h2>آزمائش کی حدیں</h2>
      <ul>
        <li>
          روزانہ زیادہ سے زیادہ {l.daily} ترسیلات (آپ کی اپنی نقل مفت ہے؛ گروپ کو کیپسول فی رکن ایک شمار ہوتا ہے)۔ اپ لوڈ: فی 24 گھنٹے {l.uploadDaily} اور {l.unusedFiles} غیر استعمال شدہ فائلیں؛
          جو فائلیں 24 گھنٹوں کے اندر کسی کیپسول میں استعمال نہ ہوں وہ حذف ہو جاتی ہیں۔
        </li>
        <li>Google، پاس ورڈ یا پاس کی سے لاگ ان کریں۔ کوئی ادائیگی نہیں، کوئی عوامی نیٹ ورک نہیں: کیپسول آزمائش کے اندر رہتے ہیں۔</li>
        <li>
          کسی ایسے پتے کو دعوت دینا جس کا پہلے سے اکاؤنٹ ہو، اس رکن کو اس کے بجائے رابطے کی درخواست بھیجتا ہے؛ اگر وہ قبول کرے تو آپ ایک دوسرے کو فالو کرتے ہیں۔ آپ کی
          اسکرین دونوں صورتوں میں ایک جیسی دکھتی ہے، اس لیے کوئی دعوتوں سے یہ معلوم نہیں کر سکتا کہ کون رکن ہے۔
        </li>
      </ul>

      <h2>ڈویلپرز کے لیے</h2>
      <p>
        سروسز <a href="/developers">Vrelis API</a> کے ذریعے کیپسول پڑھ اور بھیج سکتی ہیں۔ سروس کو ایڈمن کی سروس دعوت سے اپنا اکاؤنٹ ملتا ہے؛
        باقی سب کے لیے یہ ایک عام رکن ہے۔ یہ اپنی کلید خود بناتی ہے اور ہر کیپسول پر خود دستخط کرتی ہے؛ اس کے API ٹوکن پڑھ اور بھیج سکتے ہیں، مگر
        کلیدوں یا لاگ ان کے طریقوں کا انتظام نہیں کر سکتے۔ آپ کسی سروس کو بھی کسی اور کی طرح بلاک کر سکتے یا چھپا سکتے ہیں۔
      </p>

      <h2>ہم آزمائش کے ارکان سے کیا چاہتے ہیں</h2>
      <ol>
        <li>ایک عوامی کیپسول بھیجیں جو ایک گھنٹے میں کھلے، اور ایک نجی کیپسول کسی دوسرے رکن کو جو ایک دن میں کھلے۔</li>
        <li>عوامی کیپسول کھلنے کے بعد پڑھیں، اور کسی ایک کو پہلے پڑھنے کی کوشش کریں: آپ کو ایسا نہیں کر پانا چاہیے۔</li>
        <li>آپ میں سے ایک: دوسرے آلے پر اپنی کلید تیار کریں اور وہاں اپنے موصولہ پیغامات پڑھیں۔</li>
        <li>آپ میں سے ایک: کسی دوسرے رکن کو چھپائیں اور چیک کریں کہ اس کے کیپسول صرف آپ کے عوامی پیغامات سے غائب ہوتے ہیں۔</li>
      </ol>

      <h2>مسائل</h2>
      <p>
        ہر صفحے کے نیچے <b>مسئلہ بتائیں</b> استعمال کریں، یا {link} پر لکھیں۔ بتائیں کہ آپ نے کیا کیا، آپ کو کیا توقع تھی اور کیا ہوا؛ اسکرین شاٹ مددگار ہوتا ہے۔
        سروس، اس کی حدیں اور ذمہ داریاں <a href="/terms">استعمال کی شرائط</a> میں بیان کی گئی ہیں۔
      </p>
    </>
  );
}
